import { useI18nContext } from "@i18n/i18n-react";
import { CircleHelp, Lock, Package, ShoppingBag, ShoppingBasket } from "lucide-react";
import { QuickAction } from "./QuickAction";
import type { HubService } from "./__generated__/HomePageDataQuery.graphql";
import type { HubFilterService as Service } from "./layout/HubMap/hubFilters";

type HomeSuggestionsSectionProps = {
	onSelectHub: (service?: Service) => void;
	scannedHub?: {
		name: string;
	} | null;
	scannedHubServices?: readonly HubService[] | null;
	availableServices?: ReadonlySet<HubService>;
};

const hasService = (services: readonly HubService[] | null | undefined, service: HubService) => services?.includes(service) ?? false;

export const HomeSuggestionsSection = (props: HomeSuggestionsSectionProps) => {
	const { onSelectHub, scannedHub, scannedHubServices, availableServices } = props;
	const i18n = useI18nContext().LL["new-ui"];
	const hasInstantBuy = hasService(scannedHubServices, "INSTANT_BUY");
	// Shop & Collect is offered wherever CLICK_N_COLLECT is available.
	const hasShopNCollect = hasService(scannedHubServices, "CLICK_N_COLLECT");
	const hasClickAndCollect = hasService(scannedHubServices, "CLICK_N_COLLECT");
	const hasLock = hasService(scannedHubServices, "LOCK_B2C");

	if (scannedHub) {
		return (
			<div className="space-y-4 px-6 pt-6">
				<h2 className="font-breit text-xl font-medium leading-7 text-black">{i18n.HOME_SUGGESTIONS()}</h2>
				<div className="flex items-start gap-4 overflow-x-auto pb-1">
					{hasInstantBuy ? (
						<>
							<QuickAction
								icon={ShoppingBasket}
								label={i18n.INSTANT_BUY_TITLE()}
								params={{ hubMetaName: scannedHub.name }}
								to="/$hubMetaName/instant-buy"
							/>
						</>
					) : null}

					{hasShopNCollect ? (
						<QuickAction
							icon={ShoppingBag}
							label={i18n.SHOP_N_COLLECT_TITLE()}
							to="/$hubMetaName/shop"
							params={{ hubMetaName: scannedHub.name }}
						/>
					) : null}

					{hasClickAndCollect ? (
						<>
							<QuickAction icon={Package} label={i18n.CLICK_AND_COLLECT_TITLE()} to="/clickncollect/email" />
						</>
					) : null}

					{hasLock ? (
						<QuickAction
							icon={Lock}
							label={i18n.HOME_LOCK_PERSONAL_TITLE()}
							to="/$hubMetaName/lock"
							params={{ hubMetaName: scannedHub.name }}
						/>
					) : null}

					<QuickAction icon={CircleHelp} label={i18n.HOME_SUPPORT_ACTION()} to="/support" />
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-6 pt-6">
			<h2 className="font-breit text-xl font-medium leading-7 text-black">{i18n.HOME_SUGGESTIONS()}</h2>
			<div className="flex items-start gap-4 overflow-x-auto pb-1">
				<QuickAction icon={ShoppingBasket} label={i18n.INSTANT_BUY_TITLE()} onClick={() => onSelectHub("INSTANT_BUY")} />
				<QuickAction icon={ShoppingBag} label={i18n.SHOP_N_COLLECT_TITLE()} onClick={() => onSelectHub("SHOP_N_COLLECT")} />
				<QuickAction icon={Package} label={i18n.CLICK_AND_COLLECT_TITLE()} to="/clickncollect/email" />
				{availableServices?.has("LOCK_B2C") ? (
					<QuickAction icon={Lock} label={i18n.HOME_LOCK_PERSONAL_TITLE()} onClick={() => onSelectHub("LOCK_B2C")} />
				) : null}
				<QuickAction icon={CircleHelp} label={i18n.HOME_SUPPORT_ACTION()} to="/support" />
			</div>
		</div>
	);
};
